import {ComparatorChain} from "../../common/comparator-chain";
import {
    Address,
    Connection,
    ContractData,
    Location,
    LocationInfo,
    Meter,
    MeterType,
    Organisation,
    OrganisationInfo,
    Role,
    UserProfile
} from "@flowmaps/flowmaps-typescriptmodels";
import {toTitleCase} from "../../common/utils";
import {ContractEntity} from "../../handlers/app-entity-handler.service";
import {Entity} from "../../handlers/entity";

export class RefdataUtils {

    static organisationComparator: ComparatorChain = new ComparatorChain('info.name', 'organisationId');
    static locationsComparator: ComparatorChain = new ComparatorChain('location.info.name', 'location.info.address.street', 'location.locationId');
    static connectionsComparator: ComparatorChain = new ComparatorChain('connection.info.code', 'connection.info.connectionType', 'location.info.name', 'location.info.address.street');
    static reportsComparator: ComparatorChain = new ComparatorChain('info.name', 'reportId');
    static locationEnergyCompassYearsComparator: ComparatorChain = new ComparatorChain('year');
    static alertsComparator: ComparatorChain = new ComparatorChain('alertStatusPriority', '-timestamp',
        'meter.connection.info.code', 'alertId');
    static errorsComparator: ComparatorChain = new ComparatorChain( '-lastSeen', 'errorId');
    static enumFormatter: (value: string) => string = v => toTitleCase(v).replace(/_/g, " ");

    static authorisationOptions: string[] = [Role.owner, Role.manager, Role.viewer];

    static organisationFormatter = (organisation: Organisation) =>
        organisation ? this.organisationInfoFormatter(organisation.info) : null;

    static contractsFormatter = (contract: ContractEntity) =>
        contract ? this.contractInfoFormatter(contract.contract.contractData) : null;

    static organisationInfoFormatter = (organisationInfo: OrganisationInfo) => organisationInfo?.name;

    static contractInfoFormatter = (contractData: ContractData) => contractData?.name;

    static locationInfoFormatter = (info: LocationInfo) => info ?
        `${info.name ? info.name + ' - ' : ''}${(this.addressFormatter(info.address))}` : null;

    static locationEntityFormatter = (loc: Entity) => loc ? this.locationsFormatter(loc.location) : null;

    static locationsFormatter = (loc: Location) => loc ?
        `${loc.info.name ? loc.info.name + ' - ' : ''} ${(this.addressFormatter(loc.info.address))}` : null;

    static addressFormatter = (address: Address, includeZipCode: boolean = true) => address
        ? includeZipCode
            ? `${address.street} ${address.number}${address.addition ? ' ' + address.addition : ''}, ${address.zipCode} ${address.city}`
            : `${address.street} ${address.number}${address.addition ? ' ' + address.addition : ''}, ${address.city}`
        : "";

    static connectionFormatter = (connection: Connection) =>
        (connection.meters?.length > 0 &&
            connection.meters.every(m => m.info.type === MeterType.INTERMEDIATE || (m.info.type === MeterType.PRIMARY && m.info.virtual)) &&
            connection.info.label)
            ? connection.info.label
            : `${connection.info.code}${connection.info.label ? ` (${connection.info.label})` : ''}`;

    static userFormatter = (user: UserProfile) => user ? `${user.info.firstName + ' ' + user.info.lastName}` : null;
    static meterFormatter = (m: Entity) => {
        if (m.meter.info?.label || m.meter.details?.label) {
            return m.meter.info?.label || m.meter.details?.label;
        }
        const meterCode = m.meter.info.accessPointId;
        if (m.connection.info.connectionType === 'ChargePoint') {
            return meterCode;
        }
        const surveyor = m.meter.info.surveyor?.name;
        return `${m.connection && m.connection.info.code !== meterCode ? m.connection.info.code + ' – ' : ''}${meterCode} (${surveyor === 'EDSN' ? 'KV' : surveyor})`.trim();
    }

    static getLocationArea = (location: Location): number => location?.info.area || location?.refdata?.area;
    static getMeterArea = (meter: Meter): number => meter?.details?.area
}